import { REPORT_PUBLISHED_FORMAT } from '@/core/constants/time';

import { ReportScore } from '@/features/Reports/components/Source/Source.types';
import { ResolvedMedia } from '@/features/Reports/media-resolution/types';

import { getTimeAndDate } from '@/utils/helpers';

import { ReportCategory, ReportCountry, ReportSource, SourceCredibility } from './interfaces';

export const getReportCategoriesNames = (categories: Array<ReportCategory>): Array<string> => {
  return categories.reduce((acc, category) => {
    const subcategories = category.mainCategory.subcategories.reduce(
      (subcategoryAcc, subcategory) => [...subcategoryAcc, subcategory.name],
      [] as Array<string>
    );

    return [...acc, ...subcategories];
  }, [] as Array<string>);
};

const credibilityToReportScore: { [key in SourceCredibility]: ReportScore } = {
  [SourceCredibility.RELIABLE]: ReportScore.HIGH,
  [SourceCredibility.PARTIALLY_RELIABLE]: ReportScore.MEDIUM,
  [SourceCredibility.UNRELIABLE]: ReportScore.LOW,
  [SourceCredibility.UNKNOWN]: ReportScore.UNKNOWN,
  [SourceCredibility.EMPTY]: ReportScore.UNKNOWN,
  [SourceCredibility.DASH]: ReportScore.UNKNOWN,
};

const isSourceCredibility = (value: string): value is SourceCredibility =>
  Object.values(SourceCredibility).includes(value as SourceCredibility);

export const normalizeSourceCredibility = (
  credibility?: SourceCredibility | string | null
): SourceCredibility => {
  if (!credibility || credibility === SourceCredibility.DASH) {
    return SourceCredibility.UNKNOWN;
  }

  if (typeof credibility === 'string' && !isSourceCredibility(credibility)) {
    return SourceCredibility.UNKNOWN;
  }

  return credibility;
};

/** When Unknown is selected, also match API values that display as Unknown. */
export const expandSourceCredibilityFilterValues = (
  selected: Array<SourceCredibility>
): Array<SourceCredibility> => {
  if (!selected.includes(SourceCredibility.UNKNOWN)) {
    return [...selected];
  }

  return Array.from(new Set([...selected, SourceCredibility.EMPTY, SourceCredibility.DASH]));
};

export const reportMatchesSourceCredibility = (
  sources: Array<ReportSource>,
  selected: Array<SourceCredibility>
): boolean => {
  if (!selected.length) {
    return true;
  }

  if (!sources.length) {
    return false;
  }

  const allowed = new Set(expandSourceCredibilityFilterValues(selected));

  return sources.every(source => allowed.has(normalizeSourceCredibility(source.credibility)));
};

const getSourceHostname = (url: string): string => {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
};

export type DisplaySourceArticle = {
  id: number;
  url: string;
  title: string;
  name: string;
  publishedDatetime: string | null;
  image: string | null;
  credibility: SourceCredibility;
};

export type DisplaySourceMedia = {
  id: number;
  sourceUrl: string;
  resolvedMedia: ResolvedMedia;
  title: string;
  name: string;
  publishedDatetime: string | null;
  credibility: SourceCredibility;
};

export const normalizeSourceForDisplay = (
  source: ReportSource,
  index: number
): DisplaySourceArticle => {
  const hostname = getSourceHostname(source.url);
  const title = source.title?.trim();
  const name = source.name?.trim();

  return {
    id: source.id ?? index,
    url: source.url,
    title: title || hostname,
    name: name || hostname,
    publishedDatetime: source.publishedDatetime ?? null,
    image: source.image ?? null,
    credibility: normalizeSourceCredibility(source.credibility),
  };
};

export const extractMediaFromSources = (sources: Array<ReportSource>): Array<DisplaySourceMedia> =>
  sources.reduce<Array<DisplaySourceMedia>>((mediaItems, source, index) => {
    const article = normalizeSourceForDisplay(source, index);

    if (!source.resolvedMedia) {
      return mediaItems;
    }

    mediaItems.push({
      id: article.id,
      sourceUrl: article.url,
      resolvedMedia: source.resolvedMedia,
      title: source.resolvedMedia.title ?? article.title,
      name: article.name,
      publishedDatetime: article.publishedDatetime,
      credibility: article.credibility,
    });

    return mediaItems;
  }, []);

export const mapSourceCredibilityToReportScore = (
  credibility?: SourceCredibility | string | null
): ReportScore => {
  return credibilityToReportScore[normalizeSourceCredibility(credibility)];
};

export const formatSourcePublishedDate = (publishedDatetime?: string): string => {
  if (!publishedDatetime) {
    return '';
  }

  return getTimeAndDate({
    dateStr: publishedDatetime,
    exitFormat: REPORT_PUBLISHED_FORMAT,
  });
};

export const hasEnrichedSourceData = (source: ReportSource): boolean =>
  Boolean(source.title || source.name || source.publishedDatetime || source.credibility);

export const getReportCountriesNames = (countries: Array<ReportCountry>): string => {
  return countries
    .reduce((acc, region) => {
      const subcategories = region.region.countries.reduce(
        (countryAcc, country) => [...countryAcc, country.name],
        [] as Array<string>
      );

      return [...acc, ...subcategories];
    }, [] as Array<string>)
    .join(' - ');
};
